/**
 * sentryConfig.ts
 *
 * Decides whether Sentry reports from this page load, and under which
 * environment name. Pure, so the decision can be unit-tested without the SDK.
 */

/** What the decision needs to know about the running build and page. */
export interface SentryRuntimeInput {
  /** `window.location.hostname` of the page. */
  hostname: string;
  /** Vite mode: `import.meta.env.MODE` ("development", "dev", "production", …). */
  mode: string;
  /** `import.meta.env.PROD`: true for any `vite build`, false for the dev server. */
  prod: boolean;
}

/** The `Sentry.init` options this module owns. */
export interface SentryRuntimeOptions {
  enabled: boolean;
  environment: string;
}

/**
 * Reports whether a hostname points at the developer's own machine.
 *
 * Covers `localhost`, any `*.localhost` name, the IPv4 loopback range
 * `127.0.0.0/8` and the IPv6 loopback `::1` (with or without brackets). A
 * single trailing DNS dot (`localhost.`) is ignored.
 *
 * @param hostname - A `location.hostname` value.
 * @returns True for a loopback host.
 */
export function isLocalHost(hostname: string): boolean {
  // A fully qualified name ("localhost.") is the same host.
  const host = hostname.trim().toLowerCase().replace(/\.$/, "");
  return (
    host === "localhost" ||
    host.endsWith(".localhost") ||
    /^127(\.\d{1,3}){3}$/.test(host) ||
    host === "::1" ||
    host === "[::1]"
  );
}

/**
 * Builds the `enabled` and `environment` options for `Sentry.init`.
 *
 * Sentry reports only from a built bundle served from a real host. The Vite
 * dev server (`prod` false) and any build opened on a loopback host
 * (`vite preview`, local E2E runs) stay silent, so development errors no longer
 * land in Sentry. Without this the SDK defaulted to `environment: production`
 * for every event, dev ones included.
 *
 * @param input - The page's hostname and the Vite build flags.
 * @returns `enabled`, plus the Vite mode as the environment name.
 */
export function sentryRuntimeOptions(input: SentryRuntimeInput): SentryRuntimeOptions {
  return {
    enabled: input.prod && !isLocalHost(input.hostname),
    environment: input.mode,
  };
}
